/**
 * Context-aware logger factory
 *
 * Creates loggers with predefined context for cleaner, more organized logging.
 * Each logger instance automatically includes its context in all log entries.
 * 
 * NOTE: Currently a no-op implementation. Will be connected to Azure Application Insights
 * or other Azure-based analytics service in the future.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface ContextLogger {
  debug(message: string, metadata?: Record<string, any>): void
  info(message: string, metadata?: Record<string, any>): void
  warn(message: string, metadata?: Record<string, any>): void
  error(message: string, error?: Error, metadata?: Record<string, any>): void

  // Convenience methods for common logging patterns
  apiRequest(method: string, endpoint: string, metadata?: Record<string, any>): void
  apiResponse(method: string, endpoint: string, status: number, metadata?: Record<string, any>): void
  apiError(method: string, endpoint: string, error: Error, metadata?: Record<string, any>): void
  userAction(action: string, metadata?: Record<string, any>): void
  componentMount(componentName: string, metadata?: Record<string, any>): void
  componentError(componentName: string, error: Error, metadata?: Record<string, any>): void
}

/**
 * Create a context-aware logger
 *
 * @param context - The context identifier (e.g., 'CampaignDetails', 'ApiClient', 'AuthService')
 * @returns A logger instance with the specified context
 *
 * @example
 * ```typescript
 * const log = createLogger('CampaignDetails')
 * log.info('Loading campaign data', { campaignId: '123' })
 * log.error('Failed to load campaign', error, { campaignId: '123', retryAttempt: 2 })
 *
 * // Convenience methods
 * log.apiRequest('GET', '/campaigns/123')
 * log.userAction('campaign_created', { campaignId: '456' })
 * ```
 */
export function createLogger(context: string): ContextLogger {
  // No-op implementation for now
  // Will be replaced with Azure Application Insights integration
  
  return {
    debug(_message: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    info(_message: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    warn(_message: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    error(_message: string, _error?: Error, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    apiRequest(_method: string, _endpoint: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    apiResponse(_method: string, _endpoint: string, _status: number, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    apiError(_method: string, _endpoint: string, _error: Error, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    userAction(_action: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    componentMount(_componentName: string, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    },

    componentError(_componentName: string, _error: Error, _metadata?: Record<string, any>): void {
      // No-op - will send to Azure
    }
  }
}

/**
 * Pre-configured loggers for common contexts
 */
export const loggers = {
  api: createLogger('ApiClient'),
  auth: createLogger('AuthService'),
  ui: createLogger('UIComponents'),
  data: createLogger('DataManagement'),
  navigation: createLogger('Navigation'),
  performance: createLogger('Performance')
} as const

export default createLogger
